import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import {
  fetchAnalyticsSummary,
  fetchFleetHome,
  fetchUtilization,
  type AnalyticsParams,
  type FleetHomeParams,
  type UtilizationParams,
} from './api'
import type { AnalyticsSummaryView, FleetHomeView, UtilizationHeatmapView } from './api-types'

/**
 * Cache policy for §8.8, and the two windows are deliberately different.
 *
 * THE FLEET HOME answers "what is happening now": three of its tiles come from the
 * Redis live projection and go stale in seconds. It refetches on an interval, and the
 * interval is 30 s rather than 5 s because the same page also issues roughly ten
 * aggregate queries behind the other six tiles — polling those every five seconds
 * would put a continuous load on the continuous aggregates for a screen somebody left
 * open on a wall display.
 *
 * ANALYTICS answers "what happened over a period". A closed period cannot change and
 * an open one changes at the pace the aggregates refresh (ten minutes, per migration
 * 0002's policy), so five minutes of staleness costs nothing and saves a re-query on
 * every tab switch.
 *
 * Neither retries by default beyond TanStack's own policy: a failing subsystem already
 * degrades into a pending tile server-side, so a client-side retry storm would only
 * amplify an outage the page is designed to survive.
 */
const LIVE_REFETCH_MS = 30_000
const LIVE_STALE_MS = 15_000
const ANALYTICS_STALE_MS = 5 * 60_000

export const dashboardQueryKeys = {
  all: ['dashboard'] as const,
  fleetHome: (params: FleetHomeParams) => ['dashboard', 'fleet-home', params] as const,
  analytics: (params: AnalyticsParams) => ['dashboard', 'analytics', params] as const,
  utilization: (params: UtilizationParams) => ['dashboard', 'utilization', params] as const,
}

export function useFleetHome(params: FleetHomeParams = {}): UseQueryResult<FleetHomeView> {
  return useQuery({
    queryKey: dashboardQueryKeys.fleetHome(params),
    queryFn: () => fetchFleetHome(params),
    staleTime: LIVE_STALE_MS,
    refetchInterval: LIVE_REFETCH_MS,
  })
}

export function useAnalyticsSummary(
  params: AnalyticsParams = {},
): UseQueryResult<AnalyticsSummaryView> {
  return useQuery({
    queryKey: dashboardQueryKeys.analytics(params),
    queryFn: () => fetchAnalyticsSummary(params),
    staleTime: ANALYTICS_STALE_MS,
  })
}

/**
 * The heat map is a second request rather than a field on the summary, and stays one.
 *
 * A month of hour × weekday cells is 168 rows the summary's readers never look at, and
 * the segment selector changes the grid WITHOUT changing any figure above it — folding
 * the two together would re-fetch the whole page every time somebody switched from
 * "engins" to "véhicules routiers" on a grid.
 */
export function useUtilizationHeatmap(
  params: UtilizationParams = {},
): UseQueryResult<UtilizationHeatmapView> {
  return useQuery({
    queryKey: dashboardQueryKeys.utilization(params),
    queryFn: () => fetchUtilization(params),
    staleTime: ANALYTICS_STALE_MS,
  })
}
